/**
 * Platform API client — the post-Supabase data layer.
 *
 * Bearer access token (15 min) + rotating refresh token, both in localStorage.
 * A 401 triggers one silent refresh + retry; refresh failure clears the
 * session and surfaces as an auth error the provider reacts to.
 */

// Same-origin by default in production (nginx proxies /api to the API service).
// `??` not `||` so an intentional empty value means "same origin" — with `||`
// an empty string fell through to localhost and broke every deployed browser.
const API_BASE =
  import.meta.env.VITE_API_BASE_URL ?? (import.meta.env.DEV ? "http://localhost:8000" : "");

const ACCESS_KEY = "ngwees.access";
const REFRESH_KEY = "ngwees.refresh";

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

export const tokenStore = {
  get access() { return localStorage.getItem(ACCESS_KEY); },
  get refresh() { return localStorage.getItem(REFRESH_KEY); },
  set(access: string, refresh: string) {
    localStorage.setItem(ACCESS_KEY, access);
    localStorage.setItem(REFRESH_KEY, refresh);
  },
  clear() {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
  get hasSession() { return Boolean(localStorage.getItem(REFRESH_KEY)); },
};

let refreshPromise: Promise<boolean> | null = null;

async function refreshTokens(): Promise<boolean> {
  if (!tokenStore.refresh) return false;
  refreshPromise ??= (async () => {
    try {
      const resp = await fetch(`${API_BASE}/api/v1/platform/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: tokenStore.refresh }),
      });
      if (!resp.ok) { tokenStore.clear(); return false; }
      const data = await resp.json();
      tokenStore.set(data.access_token, data.refresh_token);
      return true;
    } catch {
      return false;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

export async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  options: { form?: FormData; retry?: boolean } = {},
): Promise<T> {
  const headers: Record<string, string> = {};
  if (tokenStore.access) headers.Authorization = `Bearer ${tokenStore.access}`;
  if (body !== undefined && !options.form) headers["Content-Type"] = "application/json";

  const resp = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: options.form ?? (body !== undefined ? JSON.stringify(body) : undefined),
  });

  if (resp.status === 401 && options.retry !== false && tokenStore.refresh) {
    if (await refreshTokens()) {
      return request<T>(method, path, body, { ...options, retry: false });
    }
  }

  const payload = await resp.json().catch(() => null);
  if (!resp.ok) {
    const detail = payload?.detail;
    const message = typeof detail === "string" ? detail : `Request failed (${resp.status})`;
    throw new ApiError(message, resp.status);
  }
  return payload as T;
}

/**
 * Fetch a file with bearer auth and one silent refresh, like `request`.
 * Returns the body plus the server's filename so downloads keep the name the
 * API chose (dated, per-dataset) instead of a generic one.
 */
export async function requestFile(
  path: string,
  retry = true,
): Promise<{ blob: Blob; filename: string | null }> {
  const headers: Record<string, string> = {};
  if (tokenStore.access) headers.Authorization = `Bearer ${tokenStore.access}`;

  const resp = await fetch(`${API_BASE}${path}`, { method: "GET", headers });

  if (resp.status === 401 && retry && tokenStore.refresh) {
    if (await refreshTokens()) return requestFile(path, false);
  }

  if (!resp.ok) {
    // Errors come back as JSON even though the happy path is a file.
    const payload = await resp.json().catch(() => null);
    const detail = payload?.detail;
    throw new ApiError(
      typeof detail === "string" ? detail : `Download failed (${resp.status})`,
      resp.status,
    );
  }

  const disposition = resp.headers.get("content-disposition") || "";
  const match = /filename="?([^";]+)"?/.exec(disposition);
  return { blob: await resp.blob(), filename: match ? match[1] : null };
}

// ============ Auth ============

export interface PlatformUser {
  id: string;
  role: string;
  phone: string | null;
  email: string | null;
  full_name: string | null;
  plan_tier: string;
  kyc_status: string;
  region_id: string | null;
  region_ids: string[];
  totp_enabled: boolean;
}

export const platformApi = {
  requestOtp: (phone: string) =>
    request<{ success: boolean }>("POST", "/api/v1/platform/auth/otp/request", { phone }),

  verifyOtp: async (phone: string, code: string) => {
    const data = await request<{
      access_token: string; refresh_token: string; is_new_user: boolean;
      user: { id: string; phone: string; role: string; kyc_status: string };
    }>("POST", "/api/v1/platform/auth/otp/verify", { phone, code });
    tokenStore.set(data.access_token, data.refresh_token);
    return data;
  },


  // ---- Passcode (works without SMS) ----
  checkPhone: (phone: string) =>
    request<{ phone: string; registered: boolean; has_passcode: boolean }>(
      "POST", "/api/v1/platform/auth/phone/check", { phone }),

  passcodeLogin: async (phone: string, passcode: string) => {
    const data = await request<{
      access_token: string; refresh_token: string;
      user: { id: string; phone: string; role: string; kyc_status: string };
    }>("POST", "/api/v1/platform/auth/passcode/login", { phone, passcode });
    tokenStore.set(data.access_token, data.refresh_token);
    return data;
  },

  passcodeRegister: async (phone: string, passcode: string, full_name?: string) => {
    const data = await request<{
      access_token: string; refresh_token: string; is_new_user: boolean;
      user: { id: string; phone: string; role: string; kyc_status: string };
    }>("POST", "/api/v1/platform/auth/passcode/register", { phone, passcode, full_name });
    tokenStore.set(data.access_token, data.refresh_token);
    return data;
  },

  setPasscode: (passcode: string, current_passcode?: string) =>
    request<{ success: boolean }>("POST", "/api/v1/platform/auth/passcode/set",
      { passcode, current_passcode }),

  me: () => request<PlatformUser>("GET", "/api/v1/platform/auth/me"),

  logout: async () => {
    const refresh = tokenStore.refresh;
    tokenStore.clear();
    if (refresh) {
      await request("POST", "/api/v1/platform/auth/logout", { refresh_token: refresh })
        .catch(() => undefined);
    }
  },

  // ============ Customer self-service ============

  regions: () =>
    request<{ regions: { id: string; name: string; code: string }[] }>(
      "GET", "/api/v1/platform/me/regions"),

  updateProfile: (body: {
    full_name?: string; nrc_number?: string; region_id?: string;
    employment_status?: string; monthly_income?: number;
  }) => request<{ success: boolean }>("PATCH", "/api/v1/platform/me/profile", body),

  quote: (amount: number, term_months: number) =>
    request<{
      amount: number; term_months: number; interest_rate: number;
      monthly_payment: number; total_repayment: number; total_cost: number;
      first_due_date: string;
    }>("POST", "/api/v1/platform/me/quote", { amount, term_months }),

  apply: (body: { amount: number; term_months: number; purpose?: string; mobile_number?: string }) =>
    request<{ id: string; status: string }>("POST", "/api/v1/platform/me/applications", body),

  myApplications: () =>
    request<{ applications: {
      id: string; amount: number; term_months: number; status: string;
      purpose: string | null; interest_rate: number | null;
      decision_reason: string | null; application_date: string | null;
      approval_date: string | null;
    }[] }>("GET", "/api/v1/platform/me/applications"),

  // ---- Notification centre ----
  notifications: (params = "") =>
    request<{
      notifications: {
        id: string; category: string; title: string; body: string;
        entity_type: string | null; entity_id: string | null;
        read: boolean; created_at: string | null;
      }[];
      total: number; unread: number;
    }>("GET", `/api/v1/platform/notifications${params}`),
  unreadCount: () =>
    request<{ unread: number }>("GET", "/api/v1/platform/notifications/unread-count"),
  markNotificationsRead: (ids?: string[]) =>
    request<{ marked: number; unread: number }>(
      "POST", "/api/v1/platform/notifications/read", ids ? { ids } : {}),
  dismissNotification: (id: string) =>
    request<{ success: boolean; unread: number }>(
      "DELETE", `/api/v1/platform/notifications/${id}`),

  // The rules the server will actually price against. The borrow screen sizes
  // itself from these so it can never offer an amount the quote would refuse.
  myLendingRules: () =>
    request<{
      tier: string; name: string; min_amount: number; max_amount: number;
      max_term_months: number; interest_rate: number;
    }>("GET", "/api/v1/platform/products/rules/me"),

  myLoans: () =>
    request<{ loans: {
      id: string; principal_amount: number; outstanding_balance: number;
      monthly_payment: number; total_amount: number; interest_rate: number;
      term_months: number; status: string; disbursement_date: string | null;
      due_date: string | null;
      installments: {
        sequence: number; due_date: string | null; amount_due: number;
        amount_paid: number; status: string;
      }[];
    }[] }>("GET", "/api/v1/platform/me/loans"),

  submitKyc: (documentType: string, front: File, back?: File) => {
    const form = new FormData();
    form.append("document_type", documentType);
    form.append("front", front);
    if (back) form.append("back", back);
    return request<{ id: string; status: string }>(
      "POST", "/api/v1/platform/me/kyc", undefined, { form });
  },

  myKyc: () =>
    request<{ kyc_status: string; latest: {
      id: string; status: string; document_type: string;
      reviewer_notes: string | null; submitted_at: string | null;
    } | null }>("GET", "/api/v1/platform/me/kyc"),

  // ============ Payments ============

  repay: (loan_id: string, amount: number, msisdn?: string) =>
    request<{ id: string; status: string; reference: string; message: string }>(
      "POST", "/api/v1/platform/payments/repay", { loan_id, amount, msisdn }),

  intentStatus: (intentId: string) =>
    request<{ id: string; status: string; status_reason: string | null }>(
      "GET", `/api/v1/platform/payments/intents/${intentId}`),
};
